import React, { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger, Button, Icon } from '@shopflo/ui'
import { CaretDown } from '@phosphor-icons/react'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import {
  PurchaseConditionType,
  PurchaseConditionAmountType,
  UiConfigDiscountType,
  CouponType,
  StandardPrerequisite,
  PrerequisiteType
} from 'src/lib/types/discountsV2'
import PurchaseConditionsProductList from './PurchaseConditionsProductList'
import PurchaseConditionsCartInputs from './PurchaseConditionsCartInputs'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'

const PurchaseConditions = () => {
  const {
    state: { uiConfig, prerequisite: rawPrerequisite },
    actions: { setFormState }
  } = useDiscountForm()
  const { t } = useLocale()

  const prerequisite = rawPrerequisite as StandardPrerequisite

  const activeConfig = prerequisite

  const [popoverOpen, setPopoverOpen] = useState<'AMOUNT' | 'QUANTITY' | null>(null)

  const handleConditionChange = (
    type: PurchaseConditionType,
    amountType: PurchaseConditionAmountType = PurchaseConditionAmountType.ENTIRE_CART
  ) => {
    if (rawPrerequisite.type === CouponType.BUNDLE) return

    let targetPrerequisiteType: PrerequisiteType = PrerequisiteType.NONE
    if (type === PurchaseConditionType.CART_AMOUNT) {
      targetPrerequisiteType = PrerequisiteType.PRICE
    } else if (type === PurchaseConditionType.CART_QUANTITY) {
      targetPrerequisiteType = PrerequisiteType.QUANTITY
    }

    const base = {
      type: prerequisite.type,
      customerEligibility: prerequisite.customerEligibility,
      usageLimit: prerequisite.usageLimit,
      stackability: prerequisite.stackability,
      availability: prerequisite.availability
    }

    let finalPrerequisite: StandardPrerequisite

    if (targetPrerequisiteType === PrerequisiteType.PRICE) {
      finalPrerequisite = {
        ...base,
        prerequisiteType: PrerequisiteType.PRICE,
        purchaseConditionType: PurchaseConditionType.CART_AMOUNT,
        purchaseConditionAmountType: amountType,
        prerequisiteSubtotalRange: 0,
        prerequisiteMaxOrderValue: null,
        prerequisiteCatalogue: [
          {
            type: PrerequisiteType.PRICE,
            price: 1,
            entities: []
          }
        ],
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    } else if (targetPrerequisiteType === PrerequisiteType.QUANTITY) {
      finalPrerequisite = {
        ...base,
        prerequisiteType: PrerequisiteType.QUANTITY,
        purchaseConditionType: PurchaseConditionType.CART_QUANTITY,
        purchaseConditionAmountType: amountType,
        prerequisiteCountToEntitlement: 0,
        prerequisiteMaxCountForEntitlement: null,
        prerequisiteCatalogue: [
          {
            type: PrerequisiteType.QUANTITY,
            quantity: 1,
            entities: []
          }
        ],
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    } else {
      finalPrerequisite = {
        ...base,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: amountType,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: null,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }

    const clearPaths = [
      'prerequisite.prerequisiteCatalogue',
      'prerequisite.prerequisiteSubtotalRange',
      'prerequisite.prerequisiteMaxOrderValue'
    ]

    // Update prerequisite with new condition type
    const updatedPrerequisite: StandardPrerequisite = finalPrerequisite

    setFormState(
      {
        prerequisite: updatedPrerequisite
      },
      clearPaths
    )
    setPopoverOpen(null)
  }

  const renderPopoverContent = (type: 'AMOUNT' | 'QUANTITY') => {
    const isAmount = type === 'AMOUNT'
    const options = [
      {
        title: isAmount ? t('discounts_v2.specific_cart_amount') : t('discounts_v2.specific_cart_quantity'),
        description: isAmount
          ? t('discounts_v2.specific_cart_amount_desc')
          : t('discounts_v2.specific_cart_quantity_desc'),
        type: PurchaseConditionAmountType.ENTIRE_CART
      },
      {
        title: isAmount ? t('discounts_v2.specific_product_amount') : t('discounts_v2.specific_product_quantity'),
        description: isAmount
          ? t('discounts_v2.specific_product_amount_desc')
          : t('discounts_v2.specific_product_quantity_desc'),
        type: PurchaseConditionAmountType.SPECIFIC_PRODUCTS
      },
      {
        title: isAmount ? t('discounts_v2.specific_collection_amount') : t('discounts_v2.specific_collection_quantity'),
        description: isAmount
          ? t('discounts_v2.specific_collection_amount_desc')
          : t('discounts_v2.specific_collection_quantity_desc'),
        type: PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
      }
    ]

    return (
      <div className='flex flex-col  divide-y-1 divide-zinc-200'>
        {options.map(opt => (
          <div
            id={`purchase-condition-${isAmount ? 'amount' : 'quantity'}-option-${opt.type
              .toLowerCase()
              .replace(/_/g, '-')}`}
            key={opt.title}
            className='flex flex-col px-1.5 py-1.5  cursor-pointer '
            onClick={() =>
              handleConditionChange(
                isAmount ? PurchaseConditionType.CART_AMOUNT : PurchaseConditionType.CART_QUANTITY,
                opt.type
              )
            }
          >
            <div className='flex flex-col px-2 py-1  rounded-lg hover:bg-zinc-50'>
              <span className='text-text-sm-regular text-zinc-700'>{opt.title}</span>
              <span className='text-text-xs-regular text-zinc-500'>{opt.description}</span>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderAmountButtonLabel = () => {
    if (activeConfig?.purchaseConditionType === PurchaseConditionType.CART_AMOUNT) {
      if (activeConfig.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_PRODUCTS) {
        return t('discounts_v2.specific_product_amount')
      }
      if (activeConfig.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS) {
        return t('discounts_v2.specific_collection_amount')
      }
    }
    return t('discounts_v2.specific_cart_amount')
  }

  const renderQuantityButtonLabel = () => {
    if (activeConfig?.purchaseConditionType === PurchaseConditionType.CART_QUANTITY) {
      if (activeConfig.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_PRODUCTS) {
        return t('discounts_v2.specific_product_quantity')
      }
      if (activeConfig.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS) {
        return t('discounts_v2.specific_collection_quantity')
      }
    }
    return t('discounts_v2.specific_cart_quantity')
  }

  const renderContent = () => {
    if (
      activeConfig?.purchaseConditionType === PurchaseConditionType.CART_AMOUNT ||
      activeConfig?.purchaseConditionType === PurchaseConditionType.CART_QUANTITY
    ) {
      if (activeConfig.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
        return <PurchaseConditionsCartInputs />
      } else {
        return <PurchaseConditionsProductList />
      }
    }
    return null
  }

  if (rawPrerequisite.type === CouponType.BUNDLE) return null
  if (!activeConfig) return null

  return (
    <div className='flex flex-col gap-3 mb-2'>
      <div className='flex flex-col gap-3'>
        <p className='text-text-sm-semibold text-zinc-800'>{t('discounts_v2.set_purchase_condition')}</p>
        <div className='flex gap-1.5'>
          <Button
            id='purchase-condition-none'
            variant='outline'
            size='md'
            onClick={() => handleConditionChange(PurchaseConditionType.NONE)}
            data-active={activeConfig?.purchaseConditionType === PurchaseConditionType.NONE ? 'true' : undefined}
          >
            <div className=' text-text-sm-medium'>{t('discounts_v2.none')}</div>
          </Button>

          <Popover open={popoverOpen === 'AMOUNT'} onOpenChange={open => setPopoverOpen(open ? 'AMOUNT' : null)}>
            <PopoverTrigger asChild>
              <Button
                id='purchase-condition-amount-trigger'
                variant='outline'
                size='md'
                data-active={
                  activeConfig?.purchaseConditionType === PurchaseConditionType.CART_AMOUNT ? 'true' : undefined
                }
              >
                <div className='flex items-center gap-2  text-text-sm-medium'>
                  {renderAmountButtonLabel()}
                  <Icon IconComponent={CaretDown} size='xxxs' iconProps={{ weight: 'bold' }} />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align='start'
              sideOffset={4}
              className='bg-white max-w-[240px] !p-0 !border-none !rounded-xl overflow-hidden'
            >
              {renderPopoverContent('AMOUNT')}
            </PopoverContent>
          </Popover>

          <Popover open={popoverOpen === 'QUANTITY'} onOpenChange={open => setPopoverOpen(open ? 'QUANTITY' : null)}>
            <PopoverTrigger asChild>
              <Button
                id='purchase-condition-quantity-trigger'
                variant='outline'
                size='md'
                data-active={
                  activeConfig?.purchaseConditionType === PurchaseConditionType.CART_QUANTITY ? 'true' : undefined
                }
                className='flex items-center !w-fit gap-0'
              >
                <div className='flex items-center gap-2 text-text-sm-medium'>
                  {renderQuantityButtonLabel()}
                  <Icon IconComponent={CaretDown} size='xxxs' iconProps={{ weight: 'bold' }} />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align='start'
              sideOffset={4}
              className='bg-white max-w-[240px] !p-0 !border-none !rounded-xl overflow-hidden'
            >
              {renderPopoverContent('QUANTITY')}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {renderContent()}
    </div>
  )
}

export default PurchaseConditions
